<template>
  <div class="def-sku">
    <ModalComponentHeader>
      {{ basketMessages?.productSpecifications }}
    </ModalComponentHeader>
    <ModalComponentBody>
      <div class="def-sku-product">
        <div>
          <DefImg :result="product.picture" />
        </div>
        <div>
          <p class="def-sku-product__type">{{ product.type }}</p>
          <p class="def-sku-product__collection">{{ product.collection?.name }}</p>
          <p class="def-sku-product__article">{{ product.article }}</p>
        </div>
      </div>

      <div v-if="sizes?.length" class="def-sku-block">
        <p class="def-sku-block__title">
          {{ sizeTitle }}:
          <span class="def-sku-block__value">{{ getActiveSize?.displayValue }}</span>
        </p>
        <DefSKUSizes :result="sizes" @changeSize="handlerChangeSize" />
      </div>

      <div v-if="product.weights?.items?.length" class="def-sku-block">
        <p class="def-sku-block__title">
          {{ weightTitle }}:
          <span v-if="isWeightSelected" class="def-sku-block__value">{{
            getActiveWeight.printWeight
          }}</span>
        </p>
        <DefSKUWeights
          :is-weight-selected="isWeightSelected"
          :product="product"
          @changeWeight="handlerChangeWeight"
        />
      </div>

      <div class="def-sku-total">
        <p class="def-sku-total__title">
          {{ basketMessages.cost }}
        </p>
        <p class="def-sku-total__price">
          {{ product.price.printPrice }}
        </p>
      </div>

      <DefBuy :result="product" :disabled="isBuyDisabled" @added="handlerAdded">
        <template #inBasket>
          <span>{{ basketMessages.inBasket }}</span>
        </template>
        {{ basketMessages.toBasket }}
      </DefBuy>
    </ModalComponentBody>
  </div>
</template>

<script lang="ts" setup>
import DefSKUWeights from '@components/Def/DefSKU/DefSKUWeights.vue';
import ModalComponentHeader from '@use/useModal/ModalComponentHeader.vue';
import ModalComponentBody from '@use/useModal/ModalComponentBody.vue';
import { IProduct } from '@/storePinia/interfaces/IProduct/IProduct';
import { ref, computed } from 'vue';
import DefSKUSizes from '@components/Def/DefSKU/DefSKUSizes.vue';
import DefBuy from '@components/Def/DefBuy/DefBuy.vue';
import { useBasketStore } from '@/storePinia/stores/basket';
import useModal from '@use/useModal/useModal';
import DefImg from '@components/Def/DefImg/DefImg.vue';
import { isProductAvailable } from '@/utils/product/product';

const props = defineProps<{
  result: IProduct;
  isWeightSelected?: boolean;
  added?: () => void;
}>();

const basketStore = useBasketStore();
const modal = useModal();

const product = ref<IProduct>(props.result);
const isWeightSelected = ref(props.isWeightSelected ?? false);

const sizes = computed(() => product.value.skuMatrix?.SIZES?.items);
const sizeTitle = computed(() => product.value.skuMatrix?.SIZES?.title);
const weightTitle = computed(() => product.value.weights?.shortTitle);
const basketMessages = computed(() => basketStore.messages);
const getActiveSize = computed(() => sizes.value?.find((item) => item.active));
const getActiveWeight = computed(() => product.value.weights?.items?.find((item) => item.active));

const handlerChangeSize = (newProduct: IProduct) => {
  product.value = newProduct;
  isWeightSelected.value = false;
};

const handlerChangeWeight = (newProduct: IProduct) => {
  product.value = newProduct;
  isWeightSelected.value = true;
};

const isBuyDisabled = computed(() => {
  if (product.value.offer?.onOrder) return true;

  if (!isProductAvailable(product.value)) return true;

  return false;
});

const handlerAdded = () => {
  modal.hide();

  if (typeof props.added === 'function') {
    props.added();
  }
};
</script>

<style lang="scss" scoped>
.def-sku {
  :deep(.def-button-primary) {
    width: 100%;
  }
}
.def-sku-block {
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  &__title {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 500;
    line-height: 125%; /* 20px */
    color: #5f5f67;
  }
  &__value {
    color: var(--color-main);
  }
}
.def-sku-total {
  display: flex;
  justify-content: space-between;
  align-items: end;
  border-top: 1px solid #cacece;
  margin-bottom: 16px;
  padding-top: 24px;
  &__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 125%;
  }
  &__price {
    font-size: 24px;
    font-weight: 600;
    line-height: 100%;
  }
}
.def-sku-product {
  display: grid;
  grid-template-columns: 80px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  margin-bottom: 32px;
  &__type {
    font-size: 14px;
    font-weight: 600;
    line-height: 100%;
    letter-spacing: 0.84px;
    text-transform: uppercase;
    margin-bottom: 6px;
    @include point-md {
      font-size: 18px;
    }
  }
  &__collection {
    font-size: 11px;
    font-weight: 400;
    line-height: 110%;
    letter-spacing: 0.88px;
    text-transform: uppercase;
    margin-bottom: 10px;
    color: #5f5f67;
    @include point-md {
      font-size: 14px;
    }
  }
  &__article {
    font-size: 11px;
    font-weight: 400;
    line-height: 110%;
    color: #5f5f67;
    @include point-md {
      font-size: 14px;
    }
  }
}
</style>
